'use client'

import {useId} from 'react'
import {benefitCardKey} from '@fitro/customer-ui/BenefitProvider'
import {useTranslation} from '../../../hooks/useTranslation'
import type {PublicGuestCheckoutPrice} from '../../../services/public-schedule.service'
import {LowestPrice30dNote} from '../promotions/PriceNotes'

/**
 * Lets a guest say they will show a benefit card on site. The card name is in every option, so it is clear
 * which card entitles to which price; the studio checks the card at the desk. A card price is a separate
 * price for card holders, never a reduction (ADR 0015): only the regular option can carry the Omnibus
 * reference of its own promotion.
 * Cards are flat entries (a brand, or one per accepted variant); `selectedCardKey` is `provider` or `provider:variant`.
 */
export function GuestBenefitCardChoice({
    price,
    selectedCardKey,
    onSelect,
    disabled = false,
}: {
    price: PublicGuestCheckoutPrice
    selectedCardKey: string | null
    onSelect: (cardKey: string | null) => void
    disabled?: boolean
}) {
    const {t, formatCurrency} = useTranslation('landing')
    const name = useId()
    const cardName = useId()
    const cards = price.benefitCards ?? []
    const selected = cards.find(card => benefitCardKey(card) === selectedCardKey) ?? null
    const money = (amountMinor: number) => formatCurrency(amountMinor / 100, {currency: price.currency})
    const option = ({key, label, checked, onChange, radioName, amountMinor, lowestPrice30dMinor}: {
        key: string
        label: string
        checked: boolean
        onChange: () => void
        radioName: string
        amountMinor?: number
        lowestPrice30dMinor?: number | null
    }) => (
        <label
            key={key}
            className="flex min-h-11 cursor-pointer items-center justify-between gap-3 rounded-[8px] border border-[var(--landing-neutral-100)] px-3 py-2 text-[14px] has-[:checked]:border-[var(--landing-primary-600)] has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60"
        >
            <span className="flex items-center gap-2">
                <input
                    type="radio"
                    name={radioName}
                    className="h-4 w-4"
                    checked={checked}
                    disabled={disabled}
                    onChange={onChange}
                />
                <span className="min-w-0 break-words">{label}</span>
            </span>
            {amountMinor === undefined ? null : (
                <span className="font-semibold tabular-nums">
                    {typeof lowestPrice30dMinor === 'number' ? (
                        <s className="mr-1 font-normal text-[var(--landing-neutral-500)]">{money(lowestPrice30dMinor)}</s>
                    ) : null}
                    {money(amountMinor)}
                </span>
            )}
        </label>
    )
    return (
        <fieldset className="m-0 min-w-0 space-y-2 border-0 p-0">
            <legend className="sr-only">
                {t('publicEntry.guestCheckout.benefitCardLegend')}
            </legend>
            {option({
                key: 'none',
                label: t('publicEntry.guestCheckout.benefitCardNone'),
                checked: !selected,
                onChange: () => onSelect(null),
                radioName: name,
                amountMinor: price.amountMinor,
                lowestPrice30dMinor: price.lowestPrice30dMinor,
            })}
            {cards.length === 1 ? option({
                key: benefitCardKey(cards[0]),
                label: t('publicEntry.guestCheckout.benefitCardOption', {card: cards[0].cardName}),
                checked: selected !== null,
                onChange: () => onSelect(benefitCardKey(cards[0])),
                radioName: name,
                amountMinor: cards[0].amountMinor,
            }) : option({
                key: 'cards',
                label: t('publicEntry.guestCheckout.benefitCardOptionMany'),
                checked: selected !== null,
                onChange: () => onSelect(benefitCardKey(cards[0])),
                radioName: name,
            })}
            {cards.length > 1 && selected ? (
                <div role="radiogroup" aria-label={t('publicEntry.guestCheckout.benefitCardChoice')} className="ml-4 space-y-2">
                    {cards.map(card => option({
                        key: benefitCardKey(card),
                        label: card.cardName,
                        checked: benefitCardKey(card) === benefitCardKey(selected),
                        onChange: () => onSelect(benefitCardKey(card)),
                        radioName: cardName,
                        amountMinor: card.amountMinor,
                    }))}
                </div>
            ) : null}
            {/* The Omnibus reference of the regular option, under the choice so the options stay one line each. */}
            {typeof price.lowestPrice30dMinor === 'number' ? (
                <LowestPrice30dNote
                    price={money(price.lowestPrice30dMinor)}
                    className="text-[12px] text-[var(--landing-neutral-500)]"
                />
            ) : null}
        </fieldset>
    )
}
